import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BookOpen, Users, MessageSquare, Layers, ArrowRight, Sparkles, Heart } from "lucide-react";
import BadgesShelf from "../components/BadgesShelf";

export default function Dashboard({ onOpenAI }) {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [subjects, setSubjects] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/subjects").then((r) => setSubjects(r.data.subjects)).catch(() => {});
  }, []);

  const mySubjects = subjects.filter((s) => (user?.subjects || []).includes(s.slug)).slice(0, 6);
  const suggested = subjects.filter((s) => !(user?.subjects || []).includes(s.slug)).slice(0, 6);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-10 animate-fade-up">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[color:var(--bee-muted)] mb-2">Hi, {user?.name?.split(" ")[0]}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Ready to focus?</h1>
      </div>

      {/* Stats bento */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard icon={BookOpen} label="Your subjects" value={stats?.subject_count ?? 0} sub={`of ${stats?.total_subjects ?? "—"} available`} />
        <StatCard icon={Users} label="Friends" value={stats?.friend_count ?? 0} sub="in your hive" />
        <StatCard icon={MessageSquare} label="Posts" value={stats?.post_count ?? 0} sub="you've authored" />
        <StatCard icon={Layers} label="Categories" value={new Set(subjects.map(s => s.category)).size} sub="to explore" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: my subjects + recent */}
        <div className="lg:col-span-2 space-y-6">
          <section className="bee-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-semibold">Your subjects</h2>
              <Link to="/subjects" data-testid="browse-subjects-link" className="text-sm text-[color:var(--bee-muted)] hover:text-[color:var(--bee-text)] flex items-center gap-1">
                Browse all <ArrowRight size={14} />
              </Link>
            </div>
            {mySubjects.length === 0 ? (
              <div className="border border-dashed border-[color:var(--bee-border)] p-8 text-center">
                <p className="text-[color:var(--bee-muted)] mb-3">You haven't joined any subjects yet.</p>
                <Link to="/subjects" data-testid="empty-browse-subjects" className="bee-btn-primary inline-block">Pick your subjects</Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {mySubjects.map((s) => (
                  <Link key={s.slug} to={`/subjects/${s.slug}`} data-testid={`my-subject-${s.slug}`} className="flex items-start gap-3 p-3 border border-[color:var(--bee-border)] hover:border-[color:var(--bee-text)] transition-colors">
                    <div className="w-10 h-10 hex-clip bg-[color:var(--bee-primary)] flex items-center justify-center flex-shrink-0 font-display font-bold text-sm">{s.name.charAt(0)}</div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{s.name}</div>
                      <div className="text-xs text-[color:var(--bee-muted)]">{s.category}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          <section className="bee-card p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Recent hive activity</h2>
            {(stats?.recent_posts || []).length === 0 ? (
              <p className="text-sm text-[color:var(--bee-muted)]">No posts yet. Be the first to start a conversation.</p>
            ) : (
              <ul className="divide-y divide-[color:var(--bee-border)]">
                {stats.recent_posts.map((p) => (
                  <li key={p.id} className="py-3">
                    <Link to={`/subjects/${p.subject_slug}`} data-testid={`recent-post-${p.id}`} className="block hover:bg-[color:var(--bee-surface-alt)] -mx-3 px-3 py-1">
                      <div className="text-sm font-semibold">{p.title}</div>
                      <div className="text-xs text-[color:var(--bee-muted)] mt-0.5">by {p.author_name} · {p.subject_slug}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Right: ai + suggested */}
        <div className="space-y-6">
          <section className="bg-[#1A1D20] text-white p-6 relative overflow-hidden">
            <Sparkles className="absolute -right-4 -top-4 text-[color:var(--bee-primary)]/20" size={100} />
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/60 mb-3">Stuck?</p>
            <h3 className="font-display text-2xl font-bold mb-2">Ask BeeAI</h3>
            <p className="text-sm text-white/70 leading-relaxed mb-5">Get instant explanations, quizzes and study plans from Claude, GPT or Gemini.</p>
            <button onClick={onOpenAI} data-testid="dashboard-open-ai" className="bee-btn-primary text-sm">Open assistant</button>
          </section>

          {user && (
            <section className="bee-card p-6">
              <BadgesShelf earned={user.earned_badges || []} />
              <Link to="/profile" data-testid="dashboard-view-badges" className="text-xs text-[color:var(--bee-muted)] hover:text-[color:var(--bee-text)] mt-3 inline-block">View all on profile →</Link>
            </section>
          )}

          <Link to="/donate" data-testid="dashboard-donate-cta" className="block bee-card p-6 bg-gradient-to-br from-[color:var(--bee-primary)]/20 to-transparent hover:from-[color:var(--bee-primary)]/30">
            <Heart size={22} className="text-[color:var(--bee-primary-hover)] fill-current mb-3" />
            <h3 className="font-display text-lg font-semibold mb-1">Keep the hive buzzing</h3>
            <p className="text-xs text-[color:var(--bee-muted)] leading-relaxed">FocusBee is free — support us and earn the <b>💛 Contributor</b> badge.</p>
          </Link>

          <section className="bee-card p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Try these</h2>
            <div className="space-y-2">
              {suggested.map((s) => (
                <Link key={s.slug} to={`/subjects/${s.slug}`} data-testid={`suggested-${s.slug}`} className="flex items-center justify-between p-2 hover:bg-[color:var(--bee-surface-alt)] group">
                  <span className="text-sm">{s.name}</span>
                  <ArrowRight size={14} className="text-[color:var(--bee-muted)] group-hover:text-[color:var(--bee-text)]" />
                </Link>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bee-card p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start justify-between mb-3">
        <Icon size={20} className="text-[color:var(--bee-muted)]" strokeWidth={2} />
      </div>
      <div className="font-display text-3xl font-bold">{value}</div>
      <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--bee-muted)] mt-1">{label}</div>
      <div className="text-[10px] text-[color:var(--bee-muted)] mt-0.5">{sub}</div>
    </div>
  );
}
